import React from 'react';
import { Loader2, Mail, Send, RefreshCw, ChevronDown, ChevronUp, BellOff, MinusCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';

export interface EmailHistoryEntry {
    recipient_email: string;
    subject: string;
    body_html: string;
    sent_at: string;
    is_initial: boolean;
    /**
     * `notice` : une MENTION, pas un mail — « Confirmé sans notification », posée quand la
     * confirmation a été enregistrée en coupant volontairement l'envoi (demande d'Emeni du 16/09).
     * Sans elle, on ouvrirait cet historique, on le trouverait vide, et on ne saurait pas pourquoi
     * le fournisseur n'a jamais rien reçu. Elle ne compte pas dans le décompte de mails affiché
     * ailleurs, qui vient du journal des envois réels.
     */
    kind?: 'email' | 'notice';
    notice_label?: string;
    /** Un vrai mail, mais ni l'envoi initial ni une relance : la réduction ou l'annulation d'une demande. */
    email_kind?: 'reduction' | 'annulation';
}

const libelleMail = (entry: EmailHistoryEntry) =>
    entry.email_kind === 'annulation' ? 'Annulation' : entry.email_kind === 'reduction' ? 'Réduction' : entry.is_initial ? 'Envoi initial' : 'Relance';

interface EmailHistoryModalProps {
    title: string;
    fetchHistory: () => Promise<EmailHistoryEntry[]>;
    onClose: () => void;
}

const fmtDateTime = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
};

// Historique des mails envoyés pour une commande/demande (envoi initial + relances) — chargé à la demande
// à l'ouverture (pas préchargé avec la liste, même pattern que l'historique des besoins Extras). L'aperçu du
// corps HTML est rendu dans une iframe sandboxée : le HTML du mail (renderEmail côté backend) porte ses
// propres styles table-based qui ne doivent pas fuiter dans l'app, et inversement.
export const EmailHistoryModal: React.FC<EmailHistoryModalProps> = ({ title, fetchHistory, onClose }) => {
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [entries, setEntries] = React.useState<EmailHistoryEntry[]>([]);
    const [expandedIdx, setExpandedIdx] = React.useState<number | null>(null);

    React.useEffect(() => {
        setLoading(true);
        fetchHistory()
            .then((rows) => {
                setEntries(rows);
                // On déplie le dernier MAIL, pas la mention « confirmé sans notification » qui peut le
                // suivre : elle n'a rien à déplier.
                const dernierMail = rows.map((r, i) => ({ r, i })).filter((x) => x.r.kind !== 'notice').pop();
                if (dernierMail) setExpandedIdx(dernierMail.i);
            })
            .catch((e: any) => setError(e?.message || "Erreur lors du chargement de l'historique."))
            .finally(() => setLoading(false));
    }, [fetchHistory]);

    const displayOrder = [...entries].reverse();

    return (
        <Modal open onClose={onClose} title={title} icon={<Mail size={16} />} maxWidth="max-w-lg"
            footer={<Button variant="secondary" onClick={onClose}>Fermer</Button>}>
            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
            ) : error ? (
                <p className="text-xs text-red-500 font-semibold">{error}</p>
            ) : entries.length === 0 ? (
                <p className="text-xs text-content-muted">Aucun mail envoyé pour l'instant.</p>
            ) : (
                <div className="space-y-2.5">
                    {displayOrder.map((entry, i) => {
                        const realIdx = entries.length - 1 - i;
                        const expanded = expandedIdx === realIdx;
                        if (entry.kind === 'notice') {
                            return (
                                <div key={realIdx} className="rounded-lg border border-border bg-surface-soft px-3 py-2.5">
                                    <p className="text-xs font-bold text-content flex items-center gap-1.5">
                                        <BellOff size={12} />
                                        {entry.notice_label || 'Confirmé sans notification'}
                                        {entry.sent_at && <span className="text-content-muted font-normal">— {fmtDateTime(entry.sent_at)}</span>}
                                    </p>
                                    <p className="text-[11px] text-content-muted mt-0.5">{/^Confirm/i.test(entry.notice_label || '') ? 'La confirmation' : 'La modification'} a été enregistrée sans qu'aucun mail ne soit envoyé.</p>
                                </div>
                            );
                        }
                        return (
                            <div key={realIdx} className="rounded-lg border border-border overflow-hidden">
                                <button onClick={() => setExpandedIdx(expanded ? null : realIdx)}
                                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 bg-surface-soft hover:bg-surface transition-colors text-left">
                                    <div className="min-w-0">
                                        <p className="text-xs font-bold text-content flex items-center gap-1.5">
                                            {entry.email_kind ? <MinusCircle size={12} /> : entry.is_initial ? <Send size={12} /> : <RefreshCw size={12} />}
                                            {libelleMail(entry)}
                                            <span className="text-content-muted font-normal">— {fmtDateTime(entry.sent_at)}</span>
                                        </p>
                                        <p className="text-[11px] text-content-muted truncate mt-0.5">À {entry.recipient_email} — {entry.subject}</p>
                                    </div>
                                    {expanded ? <ChevronUp size={14} className="shrink-0 text-content-muted" /> : <ChevronDown size={14} className="shrink-0 text-content-muted" />}
                                </button>
                                {expanded && (
                                    <iframe title={`Aperçu mail ${realIdx}`} sandbox="allow-same-origin" srcDoc={entry.body_html}
                                        className="w-full h-72 border-t border-border bg-white" />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </Modal>
    );
};
